import { API_DOMAIN } from '@/api/trpc';
import type { ExternalAuditTool, OptimizerFilterDef, OptimizerFilterGroup, ScreenerOptimizerAiAnalysisObjective } from './types';
import { RAW_RETURN_SUM_LABEL } from './returnMetricCopy';

export const externalAuditToolOptions: { value: ExternalAuditTool; label: string }[] = [
    { value: 'codex', label: 'Codex' },
    { value: 'claude_code', label: 'Claude Code' },
    { value: 'chatgpt', label: 'ChatGPT' },
    { value: 'claude', label: 'Claude (Web & Desktop)' },
    { value: 'cursor', label: 'Cursor' }
];

export const analysisObjectiveOptions: {
    value: ScreenerOptimizerAiAnalysisObjective;
    label: string;
    description: string;
    optionOnly?: boolean;
    nonOptionOnly?: boolean;
    requiresOptionEvPerSignal?: boolean;
}[] = [
    {
        value: 'balanced_options',
        label: 'Best overall',
        description: 'Ranks by robustness score first, using internal holdout behavior, option edge, breadth, and concentration.',
        optionOnly: true
    },
    {
        value: 'total_profit',
        label: RAW_RETURN_SUM_LABEL,
        description:
            'Maximizes the arithmetic sum of raw return percentages across passing signals. It is not compounded or a portfolio return.'
    },
    {
        value: 'option_ev_per_signal',
        label: 'Option EV per signal',
        description: 'Maximizes average option outcome per strategy signal, counting unexecuted option signals as zero.',
        optionOnly: true,
        requiresOptionEvPerSignal: true
    },
    {
        value: 'avg_profit_or_lift',
        label: 'Average profit / lift',
        description: 'Maximizes average outcome first and improvement over the no-screener baseline second.'
    },
    {
        value: 'option_win_rate_quality',
        label: 'Option win-rate quality',
        description: 'Maximizes executed option win rate while retaining positive expectancy and enough executed outcomes.',
        optionOnly: true
    },
    {
        value: 'entry_gating_precision',
        label: 'Entry-gating precision',
        description: 'Maximizes internal training-holdout lift and win-rate quality among entries that pass the screener.'
    },
    {
        value: 'universe_gating_breadth',
        label: 'Universe-gating breadth',
        description: 'Maximizes distinct entities and passing signals while requiring positive holdout behavior.'
    },
    {
        value: 'balanced',
        label: 'Best overall',
        description: 'Chooses the strongest all-around result across robustness, holdout behavior, and performance.',
        nonOptionOnly: true
    }
];

export const externalToolLaunchUrls: Partial<Record<ExternalAuditTool, string>> = {
    chatgpt: 'https://chatgpt.com/',
    claude: 'https://claude.ai/new',
    cursor: 'https://cursor.com/'
};

export const developerAccessPath = '/user/developerAccess';
export const project314McpEndpoint = `${API_DOMAIN}/mcp`;
export const quickAuditCandidateLimit = 8;
export const quickAuditRobustLimit = 8;
export const externalAiAuditCandidateLimit = 50;
export const externalAiAuditRobustLimit = 20;

export const OPTIMIZER_FILTER_GROUPS: { key: OptimizerFilterGroup; title: string }[] = [
    { key: 'identity', title: 'Identity' },
    { key: 'sample', title: 'Sample size' },
    { key: 'performance', title: 'Performance' },
    { key: 'robustness', title: 'Robustness' },
    { key: 'complexity', title: 'Complexity' }
];

export const OPTIMIZER_RESULT_FILTERS: OptimizerFilterDef[] = [
    {
        key: 'resultId',
        label: 'Result ID',
        chipLabel: 'ID',
        group: 'identity',
        parse: 'resultId',
        normalize: { integer: true, min: 1 },
        comparator: '=',
        unit: '',
        helpText: 'Exact optimizer result id. Useful for finding audited or promoted recommendations.',
        inputMin: 1
    },
    {
        key: 'minTradeCount',
        label: 'Min Trades (in-sample)',
        chipLabel: 'Trades',
        group: 'sample',
        parse: 'positiveInt',
        normalize: { integer: true, min: 0 },
        comparator: '≥',
        unit: '',
        helpText: 'Passing trades across the full dataset.',
        inputMin: 0
    },
    {
        key: 'minHoldoutTradeCount',
        label: 'Min Internal Holdout Trades',
        chipLabel: 'Holdout Trades',
        group: 'sample',
        parse: 'positiveInt',
        normalize: { integer: true, min: 0 },
        comparator: '≥',
        unit: '',
        helpText: ({ holdoutFractionLabel }) =>
            `Passing trades in the internal training holdout (~last ${holdoutFractionLabel} of the training window by time).`,
        inputMin: 0
    },
    {
        key: 'minOptionTradeCount',
        label: 'Min Option Trades',
        chipLabel: 'Option Trades',
        group: 'sample',
        parse: 'positiveInt',
        normalize: { integer: true, min: 0 },
        comparator: '≥',
        unit: '',
        helpText: 'Minimum executed option outcomes among passing signals.',
        inputMin: 0,
        optionOnly: true
    },
    {
        key: 'minOptionExecutionRate',
        label: 'Min Option Exec %',
        chipLabel: 'Option Exec',
        group: 'performance',
        parse: 'percent',
        normalize: { min: 0, max: 100 },
        comparator: '≥',
        unit: '%',
        helpText: 'Minimum share of passing signals with an executable option outcome.',
        inputMin: 0,
        inputMax: 100,
        optionOnly: true
    },
    {
        key: 'minOptionWinRate',
        label: 'Min Option Win Rate %',
        chipLabel: 'Option Win Rate',
        group: 'performance',
        parse: 'percent',
        normalize: { min: 0, max: 100 },
        comparator: '≥',
        unit: '%',
        helpText: 'Minimum winning share among executed option outcomes.',
        inputMin: 0,
        inputMax: 100,
        optionOnly: true
    },
    {
        key: 'minAvgProfit',
        label: 'Min Avg Trade %',
        chipLabel: 'Avg Trade',
        group: 'performance',
        parse: 'number',
        normalize: { min: -10000, max: 10000 },
        comparator: '≥',
        unit: '%',
        helpText: 'Minimum average trade P&L in percentage points.'
    },
    {
        key: 'minWinRate',
        label: 'Min Win Rate %',
        chipLabel: 'Win Rate',
        group: 'performance',
        parse: 'percent',
        normalize: { min: 0, max: 100 },
        comparator: '≥',
        unit: '%',
        helpText: 'Minimum in-sample winning trade share.',
        inputMin: 0,
        inputMax: 100
    },
    {
        key: 'minProfitFactor',
        label: 'Min Profit Factor',
        chipLabel: 'Profit Factor',
        group: 'performance',
        parse: 'number',
        normalize: { min: 0 },
        comparator: '≥',
        unit: '',
        helpText: 'Minimum gross profit divided by gross loss.',
        inputMin: 0
    },
    {
        key: 'minHoldoutLift',
        label: 'Min Holdout Lift %',
        chipLabel: 'Holdout Lift',
        group: 'robustness',
        parse: 'number',
        normalize: { min: -10000, max: 10000 },
        comparator: '≥',
        unit: '%',
        helpText: 'Minimum internal training-holdout lift versus baseline in percentage points.'
    },
    {
        key: 'minCvSignAgreement',
        label: 'Min CV Agreement %',
        chipLabel: 'CV Agreement',
        group: 'robustness',
        parse: 'percent',
        normalize: { min: 0, max: 100 },
        comparator: '≥',
        unit: '%',
        helpText: 'Minimum share of CV folds with positive lift.',
        inputMin: 0,
        inputMax: 100
    },
    {
        key: 'maxEntityProfitShare',
        label: 'Max Concentration %',
        chipLabel: 'Concentration',
        group: 'robustness',
        parse: 'percent',
        normalize: { min: 0, max: 100 },
        comparator: '≤',
        unit: '%',
        helpText: 'Maximum share of profit from one entity.',
        inputMin: 0,
        inputMax: 100
    },
    {
        key: 'minUniqueEntities',
        label: 'Min Entities',
        chipLabel: 'Entities',
        group: 'complexity',
        parse: 'positiveInt',
        normalize: { integer: true, min: 0 },
        comparator: '≥',
        unit: '',
        helpText: 'Minimum distinct entities passing the rule.',
        inputMin: 0
    },
    {
        key: 'maxConditionCount',
        label: 'Max Conditions',
        chipLabel: 'Conditions',
        group: 'complexity',
        parse: 'positiveInt',
        normalize: { integer: true, min: 0, max: 255 },
        comparator: '≤',
        unit: '',
        helpText: 'Maximum number of rule conditions.',
        inputMin: 0,
        inputMax: 255
    }
];
